import { formatCurrency } from '@erp/shared-utils';
import { flexRender } from '@/lib/tanstack-table';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { ApprovalStateCell } from './ApprovalStateCell';
import { NeedMixedCell } from './NeedMixedCell';
import { NeedNumberCell } from './NeedNumberCell';
import { NeedProductCell } from './NeedProductCell';
import { NeedQuantityCell } from './NeedQuantityCell';
import { NeedReadOnly } from './NeedReadOnly';
import { NeedSalesOrderCell } from './NeedSalesOrderCell';
import { NeedSplitNote } from './NeedSplitNote';
import { NeedUnitCostCell } from './NeedUnitCostCell';
import {
  ITEM_COLUMN_IDS,
  type LineCell,
  type LineCellContext,
  type NeedRowsValue,
} from './needRowsContext';
import { sumPoValue } from '../utils/lineFilters';
import {
  effectiveQuantity,
  isQuantityEditable,
  quantityDiff,
} from '../utils/lineQuantity';
import {
  agreedValue,
  needAgreementKey,
  type NeedGroup,
} from '../utils/needRows';
import { needSplit } from '../utils/needSplit';

function sum(
  lines: readonly AssignmentLine[],
  pick: (l: AssignmentLine) => number,
) {
  return lines.reduce((total, line) => total + pick(line), 0);
}

// One case per column the need row aggregates; tested through needRows.
// fallow-ignore-next-line complexity
export function NeedRowCell({
  need,
  needRows,
  context,
  base,
}: {
  need: NeedGroup;
  needRows: NeedRowsValue;
  context: LineCellContext;
  base: LineCell;
}) {
  const { open } = need;
  switch (context.column.id) {
    case 'product':
      return (
        <NeedProductCell
          need={need}
          line={context.row.original}
          expanded={needRows.isExpanded(need.id)}
          onToggle={() => needRows.toggle(need.id)}
        />
      );
    case 'salesOrder':
      return <NeedSalesOrderCell need={need} />;
    case 'approvalState':
      return (
        <ApprovalStateCell
          row={context.row}
          table={context.table}
          summary={approvalSummary(need)}
        />
      );
    case 'quantity':
      return (
        <span className="flex flex-col items-end gap-0.5">
          {open.some(isQuantityEditable) ? (
            <NeedQuantityCell need={need} table={context.table} />
          ) : (
            <NeedNumberCell value={sum(open, effectiveQuantity)} strong />
          )}
          <NeedSplitNote split={needSplit(open)} />
        </span>
      );
    case 'calcQuantity':
      return (
        <NeedNumberCell value={sum(open, (l) => quantityDiff(l).calculated)} />
      );
    case 'cost':
      return (
        <span className="text-xs font-medium tabular-nums text-gray-700">
          {formatCurrency(sumPoValue(open))}
        </span>
      );
    case 'unitCost':
      return <NeedUnitCostCell need={need} context={context} base={base} />;
    default:
      break;
  }
  if (ITEM_COLUMN_IDS.has(context.column.id)) {
    // Item-level values are the same on every SO line; render them from a real
    // one so links (the SO badge's detail modal) get a suggestion id, not the
    // need's display id.
    const line = open[0] ?? need.lines[0];
    return flexRender(base, {
      ...context,
      row: { ...context.row, original: line },
    });
  }
  // Per-line values: shown once when every open SO line agrees, read-only
  // here (edits stay on the SO rows), else Mixed.
  const accessor = context.column.accessorFn;
  const agreed =
    accessor == null
      ? { value: null }
      : agreedValue(open, (line) =>
          needAgreementKey(context.column.id, line, accessor(line, 0)),
        );
  return agreed ? (
    <NeedReadOnly>{flexRender(base, context)}</NeedReadOnly>
  ) : (
    <NeedMixedCell />
  );
}

/** `12/37` approved of the open SO lines when they disagree; else the code. */
function approvalSummary(need: NeedGroup): string | undefined {
  // Nothing open (all trashed or released): the row shows that state's code.
  if (need.open.length === 0) return undefined;
  if (agreedValue(need.open, (line) => line.approvalState)) return undefined;
  const approved = need.open.filter(
    (line) =>
      line.approvalState === 'approved' ||
      line.approvalState === 'auto_approved',
  ).length;
  return `${approved}/${need.open.length}`;
}
